/**
 * Cabecera SEO comun: canonical, Open Graph, Twitter Card y datos estructurados.
 *
 * Se monta una sola vez desde _app.js. Emite:
 *
 *  - <link rel='canonical'>: no existia. Con trailingSlash: true, Google puede ver
 *    /building y /building/ como dos URLs con el mismo contenido; la canonica lo
 *    resuelve explicitamente.
 *  - title y description de piso para las paginas que no definen su propio <Head>
 *    (eran 6, todas heredando el titulo de la home).
 *  - og:* y twitter:* por pagina. Antes og:image y og:type eran globales y no habia
 *    og:title ni og:description, asi que cada link compartido mostraba el texto del
 *    sitio en lugar del de la pagina.
 *  - JSON-LD: WebSite + Organization en la home, SoftwareApplication en las
 *    herramientas, BreadcrumbList en todo lo que no sea la home.
 *
 * Deliberadamente NO emite aggregateRating ni review: no hay reseñas reales que
 * respalden esos numeros, e inventarlos es motivo de accion manual de Google.
 */
import Head from 'next/head'
import { useRouter } from 'next/router'
import {
  MIGAS,
  RUTAS_HERRAMIENTA,
  RUTAS_SEO,
  SITIO,
  canonicaDeRuta,
  claveDeRuta,
  esRutaError,
  RUTAS_ERROR,
  tieneHeadPropio
} from 'src/data/general/seo'

const jsonLd = obj => JSON.stringify(obj).replace(/</g, '\\u003c')

const SeoHead = () => {
  const router = useRouter()
  const asPath = router?.asPath ?? '/'
  const clave = claveDeRuta(asPath)
  const canonica = canonicaDeRuta(asPath)
  const meta = RUTAS_SEO[clave]

  // /guides/[slug] arma su cabecera desde el frontmatter de la guia. Si emitieramos
  // la nuestra tambien, la pagina terminaria con dos <title> y dos canonical.
  if (tieneHeadPropio(clave)) return null

  // Las paginas de error se marcan noindex y sin canonical: canonicalizar /404/
  // hacia si misma le dice a Google que es una pagina real que vale indexar.
  if (esRutaError(clave)) {
    const err = RUTAS_ERROR[clave]

    return (
      <Head>
        <title>{err.titulo}</title>
        <meta name='description' content={err.descripcion} />
        <meta name='robots' content='noindex, follow' />
      </Head>
    )
  }

  const titulo = meta?.titulo ?? RUTAS_SEO['/'].titulo
  const descripcion = meta?.descripcion ?? SITIO.descripcion
  const migas = meta?.breadcrumb ?? MIGAS[clave] ?? []
  const esInicio = clave === '/'
  const esHerramienta = RUTAS_HERRAMIENTA.includes(clave)

  const grafo = []

  if (esInicio) {
    grafo.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITIO.nombre,
      url: `${SITIO.url}/`,
      description: SITIO.descripcion,
      inLanguage: 'en',
      publisher: { '@type': 'Organization', name: SITIO.nombre, url: `${SITIO.url}/`, logo: SITIO.logo }
    })
  }

  if (esHerramienta) {
    grafo.push({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: titulo.split('—')[0].trim(),
      url: canonica,
      description: descripcion,
      applicationCategory: 'GameApplication',
      applicationSubCategory: 'Calculator',
      operatingSystem: 'Web browser',
      isAccessibleForFree: true,
      inLanguage: 'en',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      author: { '@type': 'Person', name: SITIO.autor },
      publisher: { '@type': 'Organization', name: SITIO.nombre, url: `${SITIO.url}/` }
    })
  }

  if (migas.length) {
    grafo.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITIO.url}/` },
        ...migas.map((nombre, i) => ({
          '@type': 'ListItem',
          position: i + 2,
          name: nombre,
          item: i === migas.length - 1 ? canonica : undefined
        }))
      ]
    })
  }

  return (
    <Head>
      <title>{titulo}</title>
      <meta name='description' content={descripcion} />
      <link rel='canonical' href={canonica} />

      <meta property='og:locale' content='en_US' />
      <meta property='og:site_name' content={SITIO.nombre} />
      <meta property='og:type' content={esInicio ? 'website' : 'article'} />
      <meta property='og:url' content={canonica} />
      <meta property='og:title' content={titulo} />
      <meta property='og:description' content={descripcion} />
      <meta property='og:image' content={SITIO.tarjeta} />
      <meta property='og:image:width' content={String(SITIO.tarjetaAncho)} />
      <meta property='og:image:height' content={String(SITIO.tarjetaAlto)} />
      <meta property='og:image:alt' content={`${SITIO.nombre} — ${SITIO.descripcion}`} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:site' content={SITIO.twitter} />
      <meta name='twitter:title' content={titulo} />
      <meta name='twitter:description' content={descripcion} />
      <meta name='twitter:image' content={SITIO.tarjeta} />

      {grafo.map((nodo, i) => (
        <script
          key={`ld-${i}`}
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: jsonLd(nodo) }}
        />
      ))}
    </Head>
  )
}

export default SeoHead
